import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TagIcon from '@mui/icons-material/Tag';
import PeopleIcon from '@mui/icons-material/People';
import { messagingService, Channel } from '../../services/api/messaging.service';

interface ChannelBrowserProps {
  open: boolean;
  onClose: () => void;
  onJoinChannel: (channelId: string) => void;
}

const ChannelBrowser: React.FC<ChannelBrowserProps> = ({ open, onClose, onJoinChannel }) => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadChannels();
    }
  }, [open]);

  const loadChannels = async () => {
    setLoading(true);
    try {
      const result = await messagingService.getChannels();
      setChannels(result);
    } catch (error) {
      console.error('Failed to load channels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (channelId: string) => {
    try {
      await messagingService.joinChannel(channelId);
      // Update local state
      setChannels(channels.map(ch =>
        ch.id === channelId ? { ...ch, is_joined: true, member_count: ch.member_count + 1 } : ch
      ));
      onJoinChannel(channelId);
    } catch (error) {
      console.error('Failed to join channel:', error);
    }
  };

  const handleLeave = async (channelId: string) => {
    try {
      await messagingService.leaveChannel(channelId);
      setChannels(channels.map(ch =>
        ch.id === channelId ? { ...ch, is_joined: false, member_count: Math.max(0, ch.member_count - 1) } : ch
      ));
    } catch (error) {
      console.error('Failed to leave channel:', error);
    }
  };

  const filtered = channels.filter(ch =>
    !search || ch.title.toLowerCase().includes(search.toLowerCase()) ||
    ch.slug?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Browse Channels</DialogTitle>
      <DialogContent>
        <TextField
          placeholder="Search channels..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          size="small"
          style={{ marginBottom: 16 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Typography variant="body2" color="text.secondary" style={{ textAlign: 'center', paddingTop: 32, paddingBottom: 32 }}>
            Loading channels...
          </Typography>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" style={{ textAlign: 'center', paddingTop: 32, paddingBottom: 32 }}>
            No channels found
          </Typography>
        ) : (
          <List dense>
            {filtered.map((channel) => (
              <ListItem key={channel.id} style={{ borderRadius: 4, marginBottom: 4 }}>
                <TagIcon fontSize="small" style={{ marginRight: 8, color: '#999' }} />
                <ListItemText
                  primary={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {channel.title}
                      </Typography>
                      <Chip
                        icon={<PeopleIcon style={{ fontSize: 14 }} />}
                        label={channel.member_count}
                        size="small"
                        variant="outlined"
                        style={{ height: 20 }}
                      />
                    </div>
                  }
                  secondary={channel.description}
                />
                <ListItemSecondaryAction>
                  {channel.is_joined ? (
                    <Button size="small" variant="outlined" color="inherit" onClick={() => handleLeave(channel.id)}>
                      Leave
                    </Button>
                  ) : (
                    <Button size="small" variant="contained" onClick={() => handleJoin(channel.id)}>
                      Join
                    </Button>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChannelBrowser;
